/*
 * tw-recharge-overrides-v2.js
 *
 * Override script for the redesigned subscription area (tw-custom-main-product
 * section). Carries all price and sale-badge calculation logic from
 * tw-recharge-overrides.js and is structured as a clean base for the new UI.
 *
 * What it does (inherited from v1):
 *  - Strikethrough (subscription)  → data-compare-at-display (Liquid: | money
 *    in default store currency, | money_with_currency in foreign markets)
 *  - Strikethrough (one-time row)  → same compare above one-time when
 *    data-save-onetime is set (multi-quantity / bundle save)
 *  - Badge %                       → stacked saving % (e.g. "Save 14.5%")
 *  - Deliver every                 → single-plan: row hidden (no interval UI)
 *  - Tile badges                   → switch between sub/onetime values on toggle
 *
 * Data source: data-* attributes on .quantity-save-badge elements,
 * output by tw-custom-main-product.liquid at render time — no Liquid needed here.
 *
 * Subscription gifts: `#tw-rc-subscription-gifts-source` from
 * `snippets/tw-subscription-gifts.liquid` (cloned into the Recharge widget
 * shadow). Save % via `updateSubscriptionGiftsSavePct()` from the active tile.
 *
 * Subscription card bullets: Liquid outputs `#tw-rc-subscription-features-json`
 * (from `product.metafields.custom.uk_subscription_features`); injected into
 * Recharge `rc-benefits` shadow DOM with checkmark list styling.
 *
 * Widget styles: `assets/tw-recharge-widget.css` (via `window.TW_RC_STYLE_URL`
 * from tw-custom-main-product) is fetched once and injected into shadow roots.
 *
 * Lit safety: uses nodeValue on specific text nodes instead of textContent on
 * parent elements to avoid crashing Lit's ChildPart anchors inside Recharge.
 *
 * TODO (v2 additions):
 *  - Style the redesigned subscription area to match the new design (attachment 1)
 *
 * Optional: set `window.TW_RC_SKIP_FLEX_CHROME = true` before this file runs
 * to keep price-sync only (no Flexible Plan / gifts chrome). No-op if unset.
 */

(function () {
  'use strict';

  /* ─── Config ──────────────────────────────────────────────────────────── */

  const SEL = {
    widget:        'recharge-subscription-widget',
    rcBenefits:       'rc-benefits',
    subFeaturesJson: '#tw-rc-subscription-features-json',
    rcBadge:       '#rc-subscription-badge',
    strikethrough: 'span.rc-price.strike-through',
    plans:         'rc-selling-plans',
    plansSelect:   'select.rc-plans-dropdown__select',
    tileList:      '[class*="__list"]',
    variantInput:  'form[action*="/cart/add"] input[name="id"]',
    sellingPlan:   'form[action*="/cart/add"] input[name="selling_plan"]',
    tileBadge:     '.quantity-save-badge[data-option-index]',
    bundleWrap:    '[data-bundleapp-wrapper]',
    rcPurchaseOpt: 'input[name="purchaseOption"]:checked',
    bundlePlan:    'input[name="bundleapp-plan-selector-group"]:checked',
    pdpPrice:      '[data-tw-pdp-price]',
  };

  var COPY = {
    shadowMain: 'tw-rc-shadow-styles-v2',
  };

  /** @returns {boolean} */
  function skipFlexChrome() {
    return window.TW_RC_SKIP_FLEX_CHROME === true;
  }

  /**
   * `rc-radio-icon` inner dot radius (viewBox 0–24). Default r=6 ≈ 8px rendered;
   * r=8.25 ≈ 11px at the same icon size, stays centered at cx/cy 12.
   *
   * @type {string}
   */
  var RADIO_ICON_CIRCLE_R = '8.25';

  /** @type {Record<string, string>} */
  var _rcCssCache = {};

  /** @type {Record<string, Promise<string>>} */
  var _rcCssPending = {};

  /**
   * @returns {Promise<string>}
   */
  function fetchRcCss() {
    if (_rcCssCache.widget) {
      return Promise.resolve(_rcCssCache.widget);
    }
    if (_rcCssPending.widget) {
      return _rcCssPending.widget;
    }
    var url = window.TW_RC_STYLE_URL;
    if (!url) {
      return Promise.resolve('');
    }
    _rcCssPending.widget = fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error('TW RC CSS');
        return res.text();
      })
      .then(function (text) {
        _rcCssCache.widget = text;
        delete _rcCssPending.widget;
        return text;
      })
      .catch(function (err) {
        delete _rcCssPending.widget;
        console.warn('[tw-recharge-overrides-v2] CSS load failed:', err);
        return '';
      });
    return _rcCssPending.widget;
  }

  /** Preloads `tw-recharge-widget.css` for shadow injection. */
  function preloadRcStyles() {
    if (skipFlexChrome()) return;
    if (window.TW_RC_STYLE_URL) fetchRcCss();
  }

  /**
   * Appends a `<style>` with fetched CSS into a shadow root (idempotent).
   *
   * @param {ShadowRoot|DocumentFragment} root
   */
  function ensureRcShadowStyles(root) {
    if (skipFlexChrome()) return;
    if (!root || root.querySelector('style.' + COPY.shadowMain)) return;

    function inject(css) {
      if (!css || root.querySelector('style.' + COPY.shadowMain)) return;
      var st = document.createElement('style');
      st.className = COPY.shadowMain;
      st.textContent = css;
      root.appendChild(st);
    }

    if (_rcCssCache.widget) {
      inject(_rcCssCache.widget);
      return;
    }

    fetchRcCss().then(inject);
  }

  /**
   * Sizes the selected-state dot inside each `rc-radio-icon` nested shadow
   * (widget CSS cannot reach nested shadow; avoid transform — it misaligns).
   *
   * @param {ShadowRoot} shadow - `recharge-subscription-widget` shadow root
   */
  function ensureRadioIconStyles(shadow) {
    if (!shadow) return;
    shadow.querySelectorAll('rc-radio-icon').forEach(function (icon) {
      var sr = icon.shadowRoot;
      if (!sr) return;
      sr.querySelectorAll('style.tw-rc-radio-icon-style-v2').forEach(function (el) {
        el.remove();
      });
      var circle = sr.querySelector('.rc-checked-icon circle');
      if (circle && circle.getAttribute('r') !== RADIO_ICON_CIRCLE_R) {
        circle.setAttribute('r', RADIO_ICON_CIRCLE_R);
      }
    });
  }

  /**
   * Figma subscription benefit check: 8×8 stroke icon centered in a 14×14
   * #ECC84C circle (≈3px visual inset).
   *
   * @type {string}
   */
  var SUBSCRIPTION_BENEFIT_CHECK_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" ' +
    'viewBox="0 0 8 8" fill="none" aria-hidden="true">' +
    '<path d="M6.66664 2L3 5.6667L1.33334 4" stroke="#262422" ' +
    'stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  /* ─── State ───────────────────────────────────────────────────────────── */

  var _busy            = false;
  var _reapplyTimer    = null;
  var _observer        = null;
  var _pdpPriceTimer   = null;
  var _giftsMountTimer = null;

  /* ─── Utilities ───────────────────────────────────────────────────────── */

  /** Pierces into a nested custom element's shadow root */
  function nestedShadow(root, selector) {
    var el = root.querySelector(selector);
    return (el && el.shadowRoot) ? el.shadowRoot : null;
  }

  /** Recharge v2 parts use the `part` attribute; values can be a space list */
  function findByPart(root, partToken) {
    if (!root) return null;
    return root.querySelector('[part~="' + partToken + '"]') || null;
  }

  function getPurchaseOptionByPart(shadow, partToken) {
    return findByPart(shadow, 'rc-purchase-option__' + partToken);
  }

  /**
   * `querySelector` does not pierce open shadow roots (Recharge nesting).
   *
   * @param {DocumentFragment|ShadowRoot|Element} root
   * @param {string} sel
   * @returns {Element|null}
   */
  function queryDeepFrom(root, sel) {
    if (!root || !root.querySelector) return null;
    var hit = root.querySelector(sel);
    if (hit) return hit;
    if (root.shadowRoot) {
      hit = queryDeepFrom(root.shadowRoot, sel);
      if (hit) return hit;
    }
    var all = root.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.shadowRoot) {
        hit = queryDeepFrom(el.shadowRoot, sel);
        if (hit) return hit;
      }
    }
    return null;
  }

  /**
   * Insert gifts before `rc-selling-plans`, else after `rc-benefits`, else
   * append subscription card.
   *
   * @param {ShadowRoot} shadow
   * @returns {{ parent: Element, refNode: Node|null }|null}
   */
  function findSubscriptionGiftsInsertPoint(shadow) {
    var subRoot = getPurchaseOptionByPart(shadow, 'subscription');
    if (!subRoot) return null;

    var plansHost =
      queryDeepFrom(subRoot, SEL.plans) ||
      queryDeepFrom(subRoot, 'rc-selling-plans');

    var benefitsHost =
      queryDeepFrom(subRoot, SEL.rcBenefits) ||
      queryDeepFrom(subRoot, '#rc-benefits');

    if (plansHost && plansHost.parentNode) {
      return { parent: plansHost.parentNode, refNode: plansHost };
    }
    if (benefitsHost && benefitsHost.parentNode) {
      return {
        parent: benefitsHost.parentNode,
        refNode: benefitsHost.nextSibling,
      };
    }
    return { parent: subRoot, refNode: null };
  }

  /**
   * @param {ShadowRoot|Element} root
   * @param {string} id
   * @returns {HTMLElement|null}
   */
  function deepGetElementById(root, id) {
    if (!root) return null;
    if (root.getElementById) {
      var top = root.getElementById(id);
      if (top) return top;
    }
    if (!root.querySelectorAll) return null;
    var all = root.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.id === id) return el;
      if (el.shadowRoot) {
        var nested = deepGetElementById(el.shadowRoot, id);
        if (nested) return nested;
      }
    }
    return null;
  }

  /**
   * Removes trailing ISO currency codes from display strings (e.g. "£51.98 GBP"
   * → "£51.98"). UK / Markets often use | money_with_currency in Liquid or
   * Recharge widget output.
   *
   * @param {string|null|undefined} str
   * @returns {string}
   */
  function formatDisplayMoney(str) {
    if (str == null || str === '') return '';
    return String(str).replace(/\s+[A-Za-z]{3}\s*$/i, '').trim();
  }

  /**
   * Compare-at: Liquid `data-compare-at-display` (| money in default currency,
   * | money_with_currency in foreign markets). Else money + Intl fallbacks.
   */
  function formatCompareAtMoney(badge) {
    if (!badge) return null;
    var fromLiquid = badge.getAttribute('data-compare-at-display');
    if (fromLiquid != null && fromLiquid !== '') {
      return formatDisplayMoney(fromLiquid);
    }

    var pre = badge.getAttribute('data-full-price');
    var rawCents = badge.getAttribute('data-full-price-cents');
    if (rawCents == null || rawCents === '') {
      return formatDisplayMoney(pre);
    }
    var cents = parseInt(rawCents, 10);
    if (isNaN(cents)) return formatDisplayMoney(pre);

    var t = window.theme;
    var code = t && t.currency;
    if (code) {
      try {
        return formatDisplayMoney(
          new Intl.NumberFormat(
            t.locale || undefined,
            { style: 'currency', currency: code }
          ).format(cents / 100)
        );
      } catch (e) {
        // Intl not supported — fall through to raw pre value
      }
    }
    return formatDisplayMoney(pre);
  }

  function getOnetimeBlock(shadow) {
    var b = getPurchaseOptionByPart(shadow, 'onetime');
    if (b) return b;

    var i =
      shadow.querySelector('input[name="purchaseOption"][value="onetime"]') ||
      shadow.querySelector('input[name="purchaseOption"][value="oneTime"]');

    if (!i) {
      var all = shadow.querySelectorAll('input[name="purchaseOption"]');
      for (var k = 0; k < all.length; k++) {
        if ((all[k].value || '').toLowerCase() === 'onetime') {
          i = all[k];
          break;
        }
      }
    }

    if (!i) return null;
    var p = i.parentElement;
    var d = 0;
    while (p && p !== shadow && d < 8) {
      if (p.getAttribute && p.getAttribute('part')) {
        if (p.getAttribute('part').indexOf('onetime') !== -1) return p;
      }
      p = p.parentElement;
      d++;
    }
    return i.parentElement;
  }

  /** Injected or simple nodes: not managed by Lit */
  function setNonLitText(el, text) {
    if (!el) return;
    el.textContent = text == null || text === '' ? '' : String(text);
  }

  /**
   * Updates a Lit-managed text node safely using nodeValue.
   *
   * Lit stores a <!--?lit$--> comment node as its ChildPart anchor, with the
   * managed text node immediately after it. We update that text node's nodeValue
   * directly — no structural DOM changes, so Lit's reference stays valid.
   *
   * Guard: skips if value hasn't changed to avoid firing unnecessary
   * characterData mutations that would re-trigger the MutationObserver.
   */
  function setLitText(el, text) {
    if (!el || text == null) return;
    var nodes = el.childNodes;

    // Walk backwards: find last Lit comment marker, update the text node after it
    for (var i = nodes.length - 1; i >= 0; i--) {
      if (nodes[i].nodeType === 8 /* COMMENT_NODE */) {
        var next = nodes[i].nextSibling;
        if (next && next.nodeType === 3 /* TEXT_NODE */) {
          if (next.nodeValue !== text) next.nodeValue = text;
          return;
        }
        break;
      }
    }

    // Fallback: no Lit comment found — update last non-empty text node
    for (var j = nodes.length - 1; j >= 0; j--) {
      if (nodes[j].nodeType === 3 && nodes[j].nodeValue.trim()) {
        if (nodes[j].nodeValue !== text) nodes[j].nodeValue = text;
        return;
      }
    }
  }

  /** Returns the badge element for the currently checked quantity tile */
  function getActiveBadge() {
    var badges = document.querySelectorAll(SEL.tileBadge);
    for (var i = 0; i < badges.length; i++) {
      var label = badges[i].closest('label');
      if (!label) continue;
      var input = document.getElementById(label.getAttribute('for'));
      if (input && input.checked) return badges[i];
    }
    return badges.length ? badges[0] : null;
  }

  /** Returns selling plan id for the active quantity tile, if any */
  function getActiveSellingPlanId() {
    var badge = getActiveBadge();
    if (badge) {
      var fromTile = (badge.getAttribute('data-selling-plan-id') || '').trim();
      if (fromTile) return fromTile;
    }

    var sp = document.querySelector(SEL.sellingPlan);
    if (sp && sp.value) return String(sp.value).trim();

    var bp = document.querySelector(SEL.bundlePlan);
    if (bp && bp.value) return String(bp.value).trim();

    return '';
  }

  /** Returns true if the subscription purchase option is currently selected */
  function isSubscriptionSelected() {
    var sp = document.querySelector(SEL.sellingPlan);
    if (sp) return sp.value !== '';

    var bp = document.querySelector(SEL.bundlePlan);
    if (bp) return bp.value !== '';

    var widget = document.querySelector(SEL.widget);
    if (widget && widget.shadowRoot) {
      var po = widget.shadowRoot.querySelector(SEL.rcPurchaseOpt);
      if (po) return po.value === 'subscription';
    }
    return false;
  }

  /** true when the tier has bundle savings for one-time (2+ units etc.) */
  function hasOnetimeStackSavings(badge) {
    if (!badge) return false;
    var raw = (badge.getAttribute('data-save-onetime') || '').trim();
    if (raw === '') return false;
    return parseFloat(raw) > 0;
  }

  function ensureOnetimeLayoutStyle(shadow) {
    ensureRcShadowStyles(shadow);
  }

  /**
   * Puts compare (full stack) above one-time when multi-qty save applies.
   */
  function applyOnetimeComparePrice(shadow, badge) {
    var onetimeRoot = getOnetimeBlock(shadow);
    if (!onetimeRoot) return;

    var prices =
      findByPart(onetimeRoot, 'rc-purchase-option__prices') ||
      onetimeRoot.querySelector('[part*="prices"]');
    if (!prices) return;

    var hasSave = hasOnetimeStackSavings(badge);
    var compareMoney = formatCompareAtMoney(badge);

    var fromRc =
      onetimeRoot.querySelector(SEL.strikethrough) ||
      findByPart(onetimeRoot, 'rc-purchase-option__original-price');

    if (fromRc) {
      var injected = fromRc.getAttribute('data-tw-injected') === '1';
      if (hasSave && compareMoney) {
        ensureOnetimeLayoutStyle(shadow);
        injected
          ? setNonLitText(fromRc, compareMoney)
          : setLitText(fromRc, compareMoney);
        fromRc.style.display = '';
      } else {
        injected
          ? setNonLitText(fromRc, '')
          : setLitText(fromRc, '');
        fromRc.style.display = 'none';
      }
      return;
    }

    var custom = prices.querySelector('span.tw-rc-onetime-compare-v2');
    if (hasSave && compareMoney) {
      ensureOnetimeLayoutStyle(shadow);
      if (!custom) {
        custom = document.createElement('span');
        custom.className = 'rc-price strike-through tw-rc-onetime-compare-v2';
        custom.setAttribute('data-tw-injected', '1');
        prices.insertBefore(custom, prices.firstChild);
      }
      setNonLitText(custom, compareMoney);
      custom.style.display = '';
    } else {
      if (custom) {
        setNonLitText(custom, '');
        custom.style.display = 'none';
      }
    }
  }

  /**
   * Injects gift card styles into the Recharge widget shadow (once).
   *
   * @param {ShadowRoot} shadow
   */
  function ensureSubscriptionGiftsShadowCss(shadow) {
    ensureRcShadowStyles(shadow);
  }

  /**
   * @returns {HTMLElement|null}
   */
  function findSubscriptionGiftsRoot() {
    var w = document.querySelector(SEL.widget);
    if (w && w.shadowRoot) {
      var inShadow = deepGetElementById(
        w.shadowRoot,
        'tw-rc-subscription-gifts-root'
      );
      if (inShadow) return inShadow;
    }
    return document.getElementById('tw-rc-subscription-gifts-root');
  }

  /**
   * Clones `#tw-rc-subscription-gifts-source` into the subscription card.
   *
   * @param {ShadowRoot} shadow
   * @param {number} [attempt]
   */
  function mountSubscriptionGiftsInWidget(shadow, attempt) {
    attempt = attempt || 0;
    var tmpl = document.getElementById('tw-rc-subscription-gifts-source');
    if (!tmpl) return;

    ensureSubscriptionGiftsShadowCss(shadow);

    var pt = findSubscriptionGiftsInsertPoint(shadow);
    if (!pt || !pt.parent) {
      if (attempt < 80) {
        clearTimeout(_giftsMountTimer);
        _giftsMountTimer = setTimeout(function () {
          mountSubscriptionGiftsInWidget(shadow, attempt + 1);
        }, 50);
      }
      return;
    }

    var parent = pt.parent;
    var refNode = pt.refNode;

    var existing = deepGetElementById(shadow, 'tw-rc-subscription-gifts-root');
    if (
      existing &&
      existing.isConnected &&
      existing.parentNode === parent &&
      (refNode
        ? existing.nextSibling === refNode
        : parent.lastElementChild === existing)
    ) {
      updateSubscriptionGiftsVisibility();
      return;
    }
    if (existing) existing.remove();

    var frag = tmpl.content.cloneNode(true);
    var innerRoot =
      frag.querySelector('[data-tw-subscription-gifts]') ||
      frag.querySelector('.tw-rc-subscription-gifts');
    if (!innerRoot) return;
    innerRoot.id = 'tw-rc-subscription-gifts-root';
    innerRoot.setAttribute('data-tw-injected', '1');
    parent.insertBefore(innerRoot, refNode);
    updateSubscriptionGiftsVisibility();
  }

  /**
   * Updates gifts banner "Save X%" from the active quantity tile.
   */
  function updateSubscriptionGiftsSavePct() {
    var root = findSubscriptionGiftsRoot();
    if (!root) return;
    var span = root.querySelector('[data-tw-subscription-gifts-save]');
    var wrap = root.querySelector('[data-tw-subscription-gifts-save-wrap]');
    if (!span || !wrap) return;

    var badge = getActiveBadge();
    var isSub = isSubscriptionSelected();
    var pct = '';
    if (badge) {
      pct = isSub
        ? (badge.getAttribute('data-save-subscription') || '').trim()
        : (badge.getAttribute('data-save-onetime') || '').trim();
    }

    span.textContent = pct || '';
    wrap.style.display = pct ? '' : 'none';
    updateSubscriptionGiftsVisibility();
  }

  /**
   * Hides gift banner + grid when one-time purchase is selected.
   */
  function updateSubscriptionGiftsVisibility() {
    var root = findSubscriptionGiftsRoot();
    if (!root) return;

    var isSub = isSubscriptionSelected();
    root.setAttribute('data-tw-gifts-onetime', isSub ? 'false' : 'true');

    root.querySelectorAll(
      '.tw-rc-subscription-gifts__banner, .tw-rc-subscription-gifts__grid'
    ).forEach(function (el) {
      el.style.display = isSub ? '' : 'none';
    });

    root.style.display = isSub ? '' : 'none';
  }

  /* ─── Tile badge updates ──────────────────────────────────────────────── */

  /**
   * Per-bottle + per-serving lines on every multibuy card.
   * bottle  = totalPrice / bottleCount
   * serving = totalPrice / (servingsPerBottle * bottleCount)
   * Totals from existing badge attrs (data-sns-price / data-onetime-price).
   */
  function updateMultibuyValuePrices() {
    var isSub = isSubscriptionSelected();

    document.querySelectorAll(SEL.tileBadge).forEach(function (badge) {
      var label = badge.closest('label');
      if (!label) return;

      var perUnitEl = label.querySelector('[data-tw-mb-per-unit]');
      var perServingEl = label.querySelector('[data-tw-mb-per-serving]');
      if (!perUnitEl && !perServingEl) return;

      var qty = parseInt(badge.getAttribute('data-option-index') || '1', 10) || 1;
      var total = formatDisplayMoney(
        isSub
          ? (badge.getAttribute('data-sns-price') || '')
          : (badge.getAttribute('data-onetime-price') || '')
      );
      var match = total.match(/^([^0-9]*)([\d.,]+)/);
      if (!match) {
        if (perUnitEl) perUnitEl.textContent = '';
        if (perServingEl) perServingEl.textContent = '';
        return;
      }

      var amount = parseFloat(match[2].replace(',', '.'));
      if (!amount) {
        if (perUnitEl) perUnitEl.textContent = '';
        if (perServingEl) perServingEl.textContent = '';
        return;
      }

      if (perUnitEl) {
        var containerType =
          perUnitEl.getAttribute('data-container-type') ||
          (label.querySelector('[data-tw-mb-tile-data]') &&
            label
              .querySelector('[data-tw-mb-tile-data]')
              .getAttribute('data-container-type')) ||
          'bottle';
        perUnitEl.textContent =
          match[1] + (amount / qty).toFixed(2) + '/' + containerType;
      }

      if (perServingEl) {
        var dataEl = label.querySelector('[data-tw-mb-tile-data]') || badge;
        var totalServings = resolveTileTotalServings(dataEl, qty);
        if (totalServings > 0) {
          var servingLabel =
            dataEl.getAttribute('data-serving-label') || 'serving';
          perServingEl.textContent =
            match[1] +
            (amount / totalServings).toFixed(2) +
            '/' +
            servingLabel;
        } else {
          perServingEl.textContent = '';
        }
      }
    });
  }

  /** Switches tile badge text between subscription and one-time savings */
  function updateTileBadges() {
    var isSub = isSubscriptionSelected();
    document.querySelectorAll(SEL.tileBadge).forEach(function (badge) {
      var pct = isSub
        ? (badge.getAttribute('data-save-subscription') || '')
        : (badge.getAttribute('data-save-onetime') || '');

      if (pct) {
        badge.textContent = 'SAVE ' + pct + '%';
        badge.style.display = '';
      } else {
        badge.textContent = '';
        badge.style.display = 'none';
      }
    });
    updateSubscriptionGiftsSavePct();
    updateMultibuyValuePrices();
  }

  /* ─── PDP price block update ─────────────────────────────────────────── */

  /**
   * Builds the price HTML string from the computed values.
   *
   * Uses dedicated `.tw-pdp-price__*` classes styled in the section's
   * <style> block — no dependency on `tw-price.liquid` or any Shopify
   * price snippet. The block starts empty server-side so variants.js
   * (which targets id="price-{section}") cannot find or replace it.
   *
   * @param {string} currentPrice - Formatted money string for current price
   * @param {string} comparePrice - Formatted money string for compare-at
   * @param {string} savePct      - Numeric percentage string e.g. "10"
   * @returns {string} HTML to inject into [data-tw-pdp-price]
   */
  function buildPriceHtml(currentPrice, comparePrice, savePct) {
    currentPrice = formatDisplayMoney(currentPrice);
    comparePrice = formatDisplayMoney(comparePrice);

    var html = '<div class="tw-pdp-price__row">';

    if (currentPrice) {
      html += '<span class="tw-pdp-price__current">' + currentPrice + '</span>';
    }

    if (comparePrice) {
      html += '<s class="tw-pdp-price__compare">' + comparePrice + '</s>';
    }

    if (savePct) {
      html += '<span class="tw-pdp-price__badge">SAVE ' + savePct + '%</span>';
    }

    html += '</div>';
    return html;
  }

  /**
   * Fills `[data-tw-atc-price]` (main + sticky ATC) from the active quantity
   * tile + subscription vs one-time — same source as `updatePdpPrice`.
   */
  function updateAtcButtonPrice() {
    var sel =
      'product-form[data-tw-atc-inline] [data-tw-atc-price],' +
      'sticky-buy-button[data-tw-atc-inline] [data-tw-atc-price]';
    var priceNodes = document.querySelectorAll(sel);
    if (!priceNodes.length) return;

    var mainBtn = document.querySelector(
      'product-form[data-tw-atc-inline] [name="add"]'
    );
    if (mainBtn && mainBtn.disabled) {
      priceNodes.forEach(function (el) {
        el.textContent = '';
      });
      document
        .querySelectorAll(
          'product-form[data-tw-atc-inline] .tw-atc-btn__inner,' +
            'sticky-buy-button[data-tw-atc-inline] .tw-atc-btn__inner',
        )
        .forEach(function (row) {
          row.classList.add('tw-atc-btn__inner--no-price');
        });
      return;
    }

    var badge = getActiveBadge();
    var currentPrice = '';
    if (badge) {
      var isSub = isSubscriptionSelected();
      currentPrice = formatDisplayMoney(
        (isSub
          ? badge.getAttribute('data-sns-price')
          : badge.getAttribute('data-onetime-price') || '').trim()
      );
    }

    priceNodes.forEach(function (el) {
      el.textContent = currentPrice;
      var row = el.closest('.tw-atc-btn__inner');
      if (row) {
        row.classList.toggle('tw-atc-btn__inner--no-price', !currentPrice);
      }
    });
  }

  /**
   * Reads price data from the active tile badge and injects the full price
   * HTML into [data-tw-pdp-price].
   *
   * Because the block has no server-rendered content, variants.js never
   * replaces it — no flash, no MutationObserver needed.
   */
  function updatePdpPrice() {
    var priceBlock = document.querySelector(SEL.pdpPrice);
    var badge = getActiveBadge();

    if (priceBlock && badge) {
      var isSub = isSubscriptionSelected();
      var currentPrice, comparePrice, savePct;

      if (isSub) {
        currentPrice = formatDisplayMoney(
          badge.getAttribute('data-sns-price') || ''
        );
        comparePrice = formatCompareAtMoney(badge) || '';
        savePct      = badge.getAttribute('data-save-subscription') || '';
      } else {
        currentPrice = formatDisplayMoney(
          badge.getAttribute('data-onetime-price') || ''
        );
        var onetimePct = parseFloat(badge.getAttribute('data-save-onetime') || '0');
        comparePrice   = onetimePct > 0
          ? (formatCompareAtMoney(badge) || '')
          : '';
        savePct = onetimePct > 0
          ? (badge.getAttribute('data-save-onetime') || '')
          : '';
      }

      priceBlock.innerHTML = buildPriceHtml(currentPrice, comparePrice, savePct);
    }

    updateAtcButtonPrice();
  }

  /* ─── Recharge widget UI helpers ─────────────────────────────────────── */

  /**
   * Total servings/units for a pack tile.
   * Order: day supply → serving count → snippet units → servings × pack qty.
   *
   * @param {Element|null} dataEl - `[data-tw-mb-tile-data]` (or badge fallback)
   * @param {number} qty - Pack index / bottle count
   * @returns {number}
   */
  function resolveTileTotalServings(dataEl, qty) {
    if (!dataEl) return 0;
    qty = qty || 1;
    var supplyDays = parseInt(dataEl.getAttribute('data-supply-days') || '0', 10);
    var servingCount = parseInt(
      dataEl.getAttribute('data-serving-count') || '0',
      10
    );
    var snippetUnits = parseInt(
      dataEl.getAttribute('data-snippet-unit-count') || '0',
      10
    );
    var unitServings = parseInt(
      dataEl.getAttribute('data-servings-per-bottle') || '0',
      10
    );
    if (supplyDays > 0) return supplyDays;
    if (servingCount > 0) return servingCount;
    if (snippetUnits > 0) return snippetUnits;
    if (unitServings > 0) return unitServings * qty;
    return 0;
  }

  /**
   * Returns the option title of the currently checked quantity tile
   * (e.g. "60 CAPSULES") from the theme's tile DOM.
   */
  function getActiveOptionTitle() {
    var badge = getActiveBadge();
    if (!badge) return null;
    var label = badge.closest('label');
    if (!label) return null;
    var el =
      label.querySelector('.option_selector_desc .tw-mb-unit-count') ||
      label.querySelector('.option_selector_desc .option_selector_desc__title') ||
      label.querySelector(
        '.option_selector_desc span:not([data-tw-mb-tile-data]):not([hidden])'
      );
    if (!el) return null;
    var title = el.textContent.replace(/\s+/g, ' ').trim();
    return title || null;
  }

  /**
   * Computes the formatted per-serving price string for the active tile.
   *
   * Formula: selectedFinalPrice / totalServings (same rules as tile lines).
   * Servings come from `[data-tw-mb-tile-data]`; price cents from the badge.
   *
   * @returns {string|null} Formatted price string e.g. "€0.87 per serving",
   *   or null if any required data is missing/invalid.
   */
  function getPerServingText() {
    var badge = getActiveBadge();
    if (!badge) return null;
    var label = badge.closest('label');
    if (!label) return null;

    var isSub = isSubscriptionSelected();
    var priceCents;
    if (isSub) {
      priceCents = parseInt(badge.getAttribute('data-sns-price-cents') || '0', 10);
    } else {
      priceCents = parseInt(badge.getAttribute('data-full-price-cents') || '0', 10);
    }
    if (!priceCents || priceCents <= 0) return null;

    var dataEl = label.querySelector('[data-tw-mb-tile-data]') || badge;
    var qty = parseInt(badge.getAttribute('data-option-index') || '1', 10) || 1;
    var totalServings = resolveTileTotalServings(dataEl, qty);
    if (!totalServings || totalServings <= 0) return null;

    var perServingCents = priceCents / totalServings;
    var priceStr = formatDisplayMoney(
      isSub
        ? (badge.getAttribute('data-sns-price') || '')
        : (badge.getAttribute('data-onetime-price') || '')
    );
    var symbolMatch = priceStr.match(/^([^0-9]+)/);
    var symbol = symbolMatch ? symbolMatch[1] : '';
    var servingLabel = dataEl.getAttribute('data-serving-label') || 'serving';

    return symbol + (perServingCents / 100).toFixed(2) + ' per ' + servingLabel;
  }

  /**
   * Injects the "Save more with Flexible Plan" banner at the top of the
   * Recharge widget. Safe to call on every applyOverrides() tick — checks
   * for existing banner before injecting.
   *
   * @param {ShadowRoot} shadow
   */
  function injectFlexPlanBanner(shadow) {
    ensureRcShadowStyles(shadow);
  }

  /**
   * Replaces the visible "Monthly Subscription" label text with the active
   * quantity tile title (e.g. "1 Bottle") and adds a "£X.XX per serving"
   * line beneath it.
   *
   * Lit manages the "Monthly Subscription" text node directly, so we:
   *   1. Hide the original text via font-size:0 on the selector.
   *   2. Inject a <span class="tw-rc-label-group"> column container holding:
   *        - .tw-rc-option-title  — the option name ("1 Bottle")
   *        - .tw-rc-per-serving   — the per-serving price ("£0.83 per serving")
   *
   * @param {ShadowRoot} shadow
   */
  function setSubscriptionLabel(shadow) {
    var subRoot = getPurchaseOptionByPart(shadow, 'subscription');
    if (!subRoot) return;
    var selector = subRoot.querySelector('.rc-purchase-option__selector');
    if (!selector) return;

    selector.style.setProperty('align-items', 'flex-start', 'important');

    var radioIcon = selector.querySelector('rc-radio-icon');
    if (radioIcon) {
      radioIcon.style.setProperty('align-self', 'flex-start', 'important');
    }

    var title = getActiveOptionTitle();
    if (!title) return;

    // Inject label group directly after rc-radio-icon to keep zero-gap with icon.
    var group = selector.querySelector('.tw-rc-label-group');
    if (!group) {
      group = document.createElement('span');
      group.className = 'tw-rc-label-group';
      var anchor = radioIcon ? radioIcon.nextSibling : null;
      if (anchor) {
        selector.insertBefore(group, anchor);
      } else {
        selector.appendChild(group);
      }
    }

    var titleSpan = group.querySelector('.tw-rc-option-title');
    if (!titleSpan) {
      titleSpan = document.createElement('span');
      titleSpan.className = 'tw-rc-option-title';
      group.appendChild(titleSpan);
    }
    if (titleSpan.textContent !== title) titleSpan.textContent = title;

    var perServingSpan = group.querySelector('.tw-rc-per-serving');
    if (!perServingSpan) {
      perServingSpan = document.createElement('span');
      perServingSpan.className = 'tw-rc-per-serving';
      group.appendChild(perServingSpan);
    }
    var perServingText = getPerServingText();
    if (perServingText) {
      perServingSpan.textContent = perServingText;
      perServingSpan.style.display = '';
    } else {
      perServingSpan.style.display = 'none';
    }
  }

  /** Replaces Recharge default "One-time" label copy. */
  var ONETIME_LABEL_TEXT = 'One-Time Purchase';

  /**
   * Replaces the visible "One-time" label with "One-Time Purchase".
   * Same Lit-safe pattern as `setSubscriptionLabel`.
   *
   * @param {ShadowRoot} shadow
   */
  function setOnetimeLabel(shadow) {
    var oneRoot = getOnetimeBlock(shadow);
    if (!oneRoot) return;
    var selector = oneRoot.querySelector('.rc-purchase-option__selector');
    if (!selector) return;

    var group = selector.querySelector('.tw-rc-onetime-label');
    if (!group) {
      group = document.createElement('span');
      group.className = 'tw-rc-label-group tw-rc-onetime-label';
      var radioIcon = selector.querySelector('rc-radio-icon');
      if (radioIcon) {
        radioIcon.insertAdjacentElement('afterend', group);
      } else {
        selector.insertBefore(group, selector.firstChild);
      }
    }

    var titleSpan = group.querySelector('.tw-rc-option-title');
    if (!titleSpan) {
      titleSpan = document.createElement('span');
      titleSpan.className = 'tw-rc-option-title';
      group.appendChild(titleSpan);
    }
    if (titleSpan.textContent !== ONETIME_LABEL_TEXT) {
      titleSpan.textContent = ONETIME_LABEL_TEXT;
    }
  }

  /**
   * Moves the Recharge subscription badge (#rc-subscription-badge) into the
   * prices div so it sits inline: [option name] [Save X%] [compare] [price].
   * Injects layout CSS the first time to position everything correctly.
   * Badge text stays Lit-cased ("Save"); CSS text-transform makes it uppercase.
   *
   * @param {ShadowRoot} shadow
   */
  function inlineBadge(shadow) {
    var badge = shadow.querySelector(SEL.rcBadge);
    if (!badge) return;

    var subRoot = getPurchaseOptionByPart(shadow, 'subscription');
    if (!subRoot) return;

    var prices = subRoot.querySelector('.rc-purchase-option__prices');
    if (!prices) return;

    if (badge.parentElement !== prices) {
      prices.insertBefore(badge, prices.firstChild);
    }

    ensureRcShadowStyles(shadow);
  }

  /** Cached parse of `#tw-rc-subscription-features-json` (static per page) */
  var _subscriptionFeaturesParsed = null;

  var _benefitsUlObserver = null;

  /**
   * Pulls a display line from a Shopify `| json` metaobject row or a plain string.
   *
   * @param {*} row
   * @param {number} [depth]
   * @returns {string|null}
   */
  function pickFeatureTextFromMetaobjectJson(row, depth) {
    depth = depth || 0;
    if (depth > 6) return null;
    if (typeof row === 'string') {
      var t = row.trim();
      return t !== '' ? t : null;
    }
    if (!row || typeof row !== 'object') return null;

    var keys = [
      'feature',
      'title',
      'display_name',
      'name',
      'handle',
    ];
    var i;
    var k;
    var v;
    for (i = 0; i < keys.length; i++) {
      k = keys[i];
      v = row[k];
      if (typeof v === 'string' && v.trim() !== '') return v.trim();
      if (v && typeof v === 'object' && typeof v.value === 'string' && v.value.trim() !== '') {
        return v.value.trim();
      }
    }

    if (row.fields && Array.isArray(row.fields)) {
      for (i = 0; i < row.fields.length; i++) {
        var f = row.fields[i];
        if (!f) continue;
        if (f.key === 'feature' && typeof f.value === 'string' && f.value.trim() !== '') {
          return f.value.trim();
        }
      }
    }

    for (i = 0; i < keys.length; i++) {
      k = keys[i];
      v = row[k];
      if (v && typeof v === 'object') {
        var inner = pickFeatureTextFromMetaobjectJson(v, depth + 1);
        if (inner) return inner;
      }
    }

    return null;
  }

  /**
   * Accepts JSON arrays of plain strings or metaobject-shaped objects (Liquid
   * `entry | json` per list row).
   *
   * @param {Array} arr
   * @returns {string[]}
   */
  function normalizeSubscriptionFeatureRows(arr) {
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      var picked = pickFeatureTextFromMetaobjectJson(arr[i], 0);
      if (picked) out.push(picked);
    }
    return out;
  }

  /**
   * Non-empty feature lines from Liquid JSON (empty if missing or invalid).
   *
   * @returns {string[]}
   */
  function getSubscriptionFeatureLines() {
    if (_subscriptionFeaturesParsed !== null) {
      return _subscriptionFeaturesParsed;
    }
    _subscriptionFeaturesParsed = [];
    var node = document.querySelector(SEL.subFeaturesJson);
    if (!node || !node.textContent) return _subscriptionFeaturesParsed;
    try {
      var arr = JSON.parse(node.textContent.trim());
      if (Array.isArray(arr)) {
        _subscriptionFeaturesParsed = normalizeSubscriptionFeatureRows(arr);
      }
    } catch (e) {
      _subscriptionFeaturesParsed = [];
    }
    return _subscriptionFeaturesParsed;
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, function (ch) {
      if (ch === '&') return '&amp;';
      if (ch === '<') return '&lt;';
      if (ch === '>') return '&gt;';
      if (ch === '"') return '&quot;';
      return '&#39;';
    });
  }

  /**
   * Injects layout + checkmark styles into rc-benefits shadow (re-apply after Lit
   * wipes the shadow).
   *
   * @param {ShadowRoot} bsr
   */
  function injectSubscriptionBenefitsStyles(bsr) {
    if (!bsr) return;
    bsr.querySelectorAll(
      'style.tw-rc-benefits-override-v2,' +
      'style.tw-rc-benefits-override-v3,' +
      'style.tw-rc-subscription-gifts-shadow-css,' +
      'style.tw-plans-style-v2,' +
      'style.' + COPY.shadowMain
    ).forEach(function (el) {
      el.remove();
    });
    ensureRcShadowStyles(bsr);
  }

  /**
   * Locates `rc-benefits` inside the subscription purchase card (Recharge DOM
   * varies by version; several fallbacks).
   *
   * @param {ShadowRoot} shadow
   * @returns {Element|null}
   */
  function findRcBenefitsHost(shadow) {
    if (!shadow) return null;
    var subRoot = getPurchaseOptionByPart(shadow, 'subscription');
    var host = null;
    if (subRoot) {
      host = subRoot.querySelector(SEL.rcBenefits) ||
        subRoot.querySelector('#rc-benefits');
    }
    if (!host) {
      host = shadow.querySelector(
        '.rc-purchase-option__subscription rc-benefits,' +
        ' [part*="subscription"] rc-benefits,' +
        ' rc-benefits#rc-benefits'
      );
    }
    if (!host) host = shadow.querySelector(SEL.rcBenefits);
    return host;
  }

  var _benefitsRetryTimer = null;

  /**
   * Replaces Recharge default `rc-benefits` bullets with subscription feature
   * lines from Liquid JSON (see tw-custom-main-product.liquid). Retries while
   * `rc-benefits` has no `shadowRoot` (declarative shadow / late upgrade).
   *
   * @param {ShadowRoot} shadow
   * @param {number} [attempt]
   */
  function syncSubscriptionBenefits(shadow, attempt) {
    attempt = attempt || 0;
    var lines = getSubscriptionFeatureLines();
    if (!lines.length) return;

    var host = findRcBenefitsHost(shadow);
    if (!host || !host.shadowRoot) {
      if (attempt < 50) {
        clearTimeout(_benefitsRetryTimer);
        _benefitsRetryTimer = setTimeout(function () {
          syncSubscriptionBenefits(shadow, attempt + 1);
        }, 100);
      }
      return;
    }

    var bsr = host.shadowRoot;
    injectSubscriptionBenefitsStyles(bsr);

    var ul = bsr.querySelector('ul');
    if (!ul) {
      if (attempt < 50) {
        clearTimeout(_benefitsRetryTimer);
        _benefitsRetryTimer = setTimeout(function () {
          syncSubscriptionBenefits(shadow, attempt + 1);
        }, 100);
      }
      return;
    }

    var sig = JSON.stringify(lines);
    var firstExpected = lines[0] ? lines[0].replace(/\s+/g, ' ').trim() : '';
    var li0 = ul.querySelector('li');
    var firstActual = li0
      ? li0.textContent.replace(/\s+/g, ' ').trim()
      : '';
    var synced = ul.getAttribute('data-tw-benefits-sync') === sig;
    if (synced && firstActual === firstExpected && lines.length > 0) return;

    ul.setAttribute('data-tw-benefits-sync', sig);
    ul.classList.add('tw-rc-benefits__ul');
    ul.innerHTML = lines
      .map(function (line) {
        return (
          '<li>' +
          '<span class="tw-rc-benefit-check">' +
          SUBSCRIPTION_BENEFIT_CHECK_SVG +
          '</span>' +
          '<span class="tw-rc-benefit-label">' +
          escapeHtml(line.trim()) +
          '</span>' +
          '</li>'
        );
      })
      .join('');
  }


  /**
   * Recharge renders prices with money_with_currency (e.g. "£51.98 GBP").
   * Overwrite widget price nodes from tile badge data (symbol only).
   *
   * @param {ShadowRoot} shadow
   * @param {HTMLElement|null} badge
   */
  function syncRechargeWidgetDisplayPrices(shadow, badge) {
    if (!badge) return;

    var sns = formatDisplayMoney(badge.getAttribute('data-sns-price') || '');
    var otp = formatDisplayMoney(badge.getAttribute('data-onetime-price') || '');
    var compare = formatCompareAtMoney(badge);

    var subRoot = getPurchaseOptionByPart(shadow, 'subscription');
    if (subRoot) {
      var discounted = findByPart(subRoot, 'rc-purchase-option__discounted-price');
      if (discounted && sns) {
        setLitText(discounted, sns);
      }

      var strike =
        subRoot.querySelector(SEL.strikethrough) ||
        findByPart(subRoot, 'rc-purchase-option__original-price');
      if (strike && compare) {
        if (strike.getAttribute('data-tw-injected') === '1') {
          setNonLitText(strike, compare);
        } else {
          setLitText(strike, compare);
        }
      }
    }

    var oneRoot = getOnetimeBlock(shadow);
    if (oneRoot) {
      var onePrice = findByPart(oneRoot, 'rc-purchase-option__price');
      if (onePrice && otp) {
        setLitText(onePrice, otp);
      }
    }
  }

  /* ─── Recharge widget overrides ───────────────────────────────────────── */

  function applyOverrides() {
    if (_busy) return;

    var widget = document.querySelector(SEL.widget);
    if (!widget || !widget.shadowRoot) return;

    var shadow  = widget.shadowRoot;
    var badge   = getActiveBadge();
    var savePct = badge ? (badge.getAttribute('data-save-subscription') || '') : '';

    _busy = true;

    // 1. Subscription: compare line = full stack (unit one-time × qty)
    (function setSubscriptionCompare() {
      var subRoot = getPurchaseOptionByPart(shadow, 'subscription');
      var el = subRoot
        ? (subRoot.querySelector(SEL.strikethrough) ||
           findByPart(subRoot, 'rc-purchase-option__original-price'))
        : null;
      if (!el) el = shadow.querySelector(SEL.strikethrough);
      if (el) {
        setLitText(el, badge ? formatCompareAtMoney(badge) : null);
      }
    }());

    // 2. One-time row: show compare above one-time when tier has savings
    applyOnetimeComparePrice(shadow, badge);

    // 2b. Strip trailing currency codes from Recharge price nodes (e.g. "GBP")
    syncRechargeWidgetDisplayPrices(shadow, badge);

    // 3. Widget badge % → stacked saving. Match Recharge/Lit casing ("Save")
    //    so MutationObserver + Lit re-renders do not fight over "SAVE" vs
    //    "Save" (causes badge flicker). Visual uppercase via CSS.
    setLitText(
      shadow.querySelector(SEL.rcBadge),
      savePct ? 'Save ' + savePct + '%' : null
    );

    if (skipFlexChrome()) {
      var giftsRoot = shadow.querySelector('#tw-rc-subscription-gifts-root');
      if (giftsRoot) giftsRoot.remove();

      updateAtcButtonPrice();

      var skipPlansShadow = nestedShadow(shadow, SEL.plans);
      if (skipPlansShadow) {
        var skipPlansHost = skipPlansShadow.host;
        var skipSelect = skipPlansShadow.querySelector(SEL.plansSelect);
        if (skipSelect && skipSelect.options.length === 1) {
          if (skipPlansHost) skipPlansHost.style.display = 'none';
        } else if (skipPlansHost && skipSelect && skipSelect.options.length > 1) {
          skipPlansHost.style.display = '';
        }
      }

      _busy = false;
      return;
    }

    // 4. Banner, option label, badge position
    injectFlexPlanBanner(shadow);
    ensureRadioIconStyles(shadow);
    setSubscriptionLabel(shadow);
    setOnetimeLabel(shadow);
    inlineBadge(shadow);
    syncSubscriptionBenefits(shadow);
    mountSubscriptionGiftsInWidget(shadow);
    updateSubscriptionGiftsSavePct();
    updateAtcButtonPrice();

    // 5. Deliver every — `rc-selling-plans` nested shadow. When there is only
    //    one option, hide the whole row (label + select); Recharge still keeps
    //    the selected option in the DOM for cart.
    var pShadow = nestedShadow(shadow, SEL.plans);
    if (pShadow) {
      var plansHost = pShadow.host;
      var select = pShadow.querySelector(SEL.plansSelect);
      if (select && select.options.length === 1) {
        // Re-inject every time — Lit wipes rc-selling-plans shadow on toggle.
        ensureRcShadowStyles(pShadow);
        if (plansHost) plansHost.style.display = 'none';
      } else if (plansHost && select && select.options.length > 1) {
        plansHost.style.display = '';
      }
    }

    _busy = false;
    setupBenefitsListObserver(shadow);
  }

  /* ─── Observer ────────────────────────────────────────────────────────── */

  /**
   * Targeted MutationObserver — watches only the elements we override.
   *
   * Two observation scopes:
   * 1. Shadow root (childList + subtree) — catches full Lit re-renders including
   *    nested custom elements recreated on subscription/one-time toggle.
   * 2. Specific override targets (childList + subtree + characterData) —
   *    catches Lit reverting our nodeValue changes on those elements only.
   */
  function setupObserver(shadow) {
    if (_observer) _observer.disconnect();

    _observer = new MutationObserver(function () {
      if (_busy) return;
      clearTimeout(_reapplyTimer);

      // Strip GBP before next paint so it never flashes on screen.
      // rAF fires after Lit's synchronous DOM update but before the browser
      // composites the frame, so the user never sees the raw currency code.
      var badge = getActiveBadge();
      if (badge) {
        requestAnimationFrame(function () {
          syncRechargeWidgetDisplayPrices(shadow, badge);
        });
      }

      _reapplyTimer = setTimeout(applyOverrides, 80);
    });

    _observer.observe(shadow, { childList: true, subtree: true });

    var subOpts = { childList: true, subtree: true, characterData: true };
    var b = shadow.querySelector(SEL.rcBadge);
    if (b) _observer.observe(b, subOpts);

    shadow.querySelectorAll(SEL.strikethrough).forEach(function (el) {
      _observer.observe(el, subOpts);
    });

    var oneRoot = getOnetimeBlock(shadow);
    if (oneRoot) {
      var onePr =
        findByPart(oneRoot, 'rc-purchase-option__prices') ||
        oneRoot.querySelector('[part*="prices"]');
      if (onePr) _observer.observe(onePr, subOpts);
      var oneCmp = oneRoot.querySelector('span.tw-rc-onetime-compare-v2');
      if (oneCmp) _observer.observe(oneCmp, subOpts);
    }
  }

  /**
   * Watches the Recharge `rc-benefits` list so Lit cannot permanently revert
   * our copy — we re-sync on the next microtask after DOM mutations.
   *
   * @param {ShadowRoot} shadow
   */
  function setupBenefitsListObserver(shadow) {
    if (_benefitsUlObserver) {
      _benefitsUlObserver.disconnect();
      _benefitsUlObserver = null;
    }

    function tryObserve(attempt) {
      attempt = attempt || 0;
      var host = findRcBenefitsHost(shadow);
      if (!host || !host.shadowRoot) {
        if (attempt < 80) requestAnimationFrame(function () { tryObserve(attempt + 1); });
        return;
      }
      var ul = host.shadowRoot.querySelector('ul');
      if (!ul) {
        if (attempt < 80) requestAnimationFrame(function () { tryObserve(attempt + 1); });
        return;
      }

      _benefitsUlObserver = new MutationObserver(function () {
        if (_busy) return;
        queueMicrotask(function () {
          syncSubscriptionBenefits(shadow, 0);
        });
      });
      _benefitsUlObserver.observe(ul, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    tryObserve(0);
  }

  /* ─── Widget init ─────────────────────────────────────────────────────── */

  function initWidget() {
    var widget = document.querySelector(SEL.widget);
    if (!widget) return;

    function tryStart() {
      if (!widget.shadowRoot || !widget.shadowRoot.querySelector('.rc-selection__root')) {
        return false;
      }
      applyOverrides();
      setupObserver(widget.shadowRoot);
      return true;
    }

    if (!tryStart()) {
      var waitObs = new MutationObserver(function (_, obs) {
        if (tryStart()) obs.disconnect();
      });
      waitObs.observe(widget.shadowRoot || widget, { childList: true, subtree: true });
      // Fallback timers in case MutationObserver misses the initial render
      [800, 2000].forEach(function (ms) { setTimeout(tryStart, ms); });
    }
  }

  /* ─── Event wiring ────────────────────────────────────────────────────── */

  function initEvents() {
    // Subscription / bundleapp plan toggle → update tile badges + pdp price
    document.addEventListener('change', function (e) {
      if (!e.target) return;
      if (
        e.target.name === 'bundleapp-plan-selector-group' ||
        e.target.name === 'selling_plan'
      ) {
        updateTileBadges();
        setTimeout(applyOverrides, 80);
        setTimeout(updatePdpPrice, 80);
      }
    });

    // Clicks inside Recharge widget or bundleapp → update tile badges + pdp price
    // + re-apply overrides after Lit finishes re-rendering on sub/one-time toggle.
    // 350ms gives Recharge time to complete its state update before we override.
    document.addEventListener('click', function (e) {
      if (!e.target) return;
      if (e.target.closest(SEL.bundleWrap) || e.target.closest(SEL.widget)) {
        setTimeout(updateTileBadges, 80);
        setTimeout(updatePdpPrice, 80);
        setTimeout(applyOverrides, 350);
        setTimeout(updatePdpPrice, 400);
      }
    });

    // Quantity tile radio change → re-apply overrides + notify Recharge of variant change
    document.addEventListener('change', function (e) {
      if (!e.target || !e.target.closest) return;
      if (
        e.target.closest(SEL.tileList) &&
        e.target.name !== 'bundleapp-plan-selector-group' &&
        e.target.name !== 'selling_plan' &&
        e.target.name !== 'rc_plan'
      ) {
        updateTileBadges();
        updatePdpPrice();
        setTimeout(applyOverrides, 80);
        // Dispatch change on variant ID input so Recharge re-renders its prices
        setTimeout(function () {
          var formEl = e.target.closest('form');
          var idInput = formEl
            ? formEl.querySelector('input[name="id"]')
            : null;
          if (idInput) {
            idInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }, 50);
      }
    });

    // BundleApp ready event
    document.addEventListener('bundleapp:ready', function () {
      setTimeout(updateTileBadges, 700);
      setTimeout(updatePdpPrice, 700);
      setTimeout(applyOverrides, 900);
    });

    // Shopify variant change (size / colour picker) — variants.js fires after
    // updating input[name="id"]. Give BundleApp 300 ms to update badge data.
    document.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'id') {
        clearTimeout(_pdpPriceTimer);
        _pdpPriceTimer = setTimeout(function () {
          updatePdpPrice();
        }, 300);
      }
    });

    // Initial / retry: tiles exist without BundleApp wrap on many PDPs
    if (document.querySelector(SEL.tileBadge)) {
      setTimeout(updateTileBadges, 200);
      setTimeout(updateTileBadges, 800);
      setTimeout(updateTileBadges, 1500);
    }
  }

  /* ─── Boot ────────────────────────────────────────────────────────────── */

  function boot() {
    preloadRcStyles();
    initEvents();
    initWidget();
    // Multibuy value lines (per-bottle / per-serving) — do not wait for tile click
    updateMultibuyValuePrices();
    updateTileBadges();
    // Initial render — runs after BundleApp / Recharge have settled
    setTimeout(updatePdpPrice, 900);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /** @public — used by custom PDP scripts (tw-custom-main-product). */
  window.twCustomPdpV2 = {
    refreshAtcPrice: updateAtcButtonPrice,
    isSubscriptionSelected: isSubscriptionSelected,
    getActiveSellingPlanId: getActiveSellingPlanId,
  };
})();
